import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { BaseReactComponent, EmptyProps } from "@/Common/Components/BaseReactComponent";
import { AsyncLoaderControl } from "@/Common/Components/Loader/AsyncLoaderControl";
import { Popup } from "@/Common/Components/Modals/Popup";
import { MyleLogo } from "@/Common/Components/MyleLogo/MyleLogo";
import { Toast } from "@/Common/Components/Toast/Toast";
import { Locale } from "@/Common/Models/Localization/Locale";
import { ILoginModel } from "@/Common/Models/UI/Login/ILoginModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { ResourceProvider } from "@/Common/Resources/StaticResources";
import { LoginPage as MyleResoucesForLoginPage } from "@/Common/Resources/StaticResourcesFor";
import { Dispatcher } from "@/Common/Stores/Dispatcher";
import { LoginWorkflow } from "@/Common/Workflow/LoginWorkflow/LoginWorkflow";
import { RequestResetPasswordControl } from "../PasswordResetApp/RequestResetPasswordControl";
import { LoginAuthenticatorCodeView } from "./Login/LoginAuthenticatorCodeView";
import { LoginCredentialsView } from "./Login/LoginCredentialsView";
import { LoginWorkflowPopups } from "./Login/LoginWorkflowPopups";
import { VersionCheck } from "./VersionCheck";

import "./LoginPage.css";

declare let currentLang: string;

class LoginPageState {
    public model: ILoginModel;
    public frontpageCampaignInError: boolean;
    public frontpageCampaignId: number;
    public loginWorkflow: LoginWorkflow;
    public password: string;
}

class LoginPage extends BaseReactComponent<EmptyProps, LoginPageState> {
    private language: Locale;
    private boundEventId: string;
    private resources: MyleResoucesForLoginPage = new MyleResoucesForLoginPage();
    private stringProvider: ResourceProvider;

    constructor(props: EmptyProps) {
        super(props);
        this.language = this.getFirstLanguage(currentLang);
        this.stringProvider = new ResourceProvider(this.resources, this.language);

        const initialState = new LoginPageState();
        initialState.model = sessionStore.clientLoginModel;
        initialState.loginWorkflow = sessionStore.loginWorkflow;
        this.state = initialState;
    }

    public componentDidMount(): void {
        this.getFrontPageCampaign();

        this.boundEventId = sessionStore.AddChangeListener(this.storeChangeListener, this);
        $(window).on("resize.login", () => this.onResize());
        $("#user_name").focus();

        let h = this.maxHeight();
        $("#myle-wall").css({ height: h + "px" });
    }

    public componentDidUpdate(prevProps: EmptyProps, prevState: LoginPageState): void {
        let lang = this.getFirstLanguage(this.language);

        // set campaign image
        if (this.state.frontpageCampaignId && !this.state.frontpageCampaignInError && this.state.frontpageCampaignId != prevState.frontpageCampaignId)
            $("#myle-wall-if").attr("src", `https://demo.medfarsolutions.com/content/CampaignImages/${this.state.frontpageCampaignId}/${lang}.html`);
        else if (!this.state.frontpageCampaignId || this.state.frontpageCampaignInError)
            $("#myle-wall-if").attr("src", `https://demo.medfarsolutions.com/content/Myle_HomePage_${lang}.html`);
    }

    public componentWillUnmount(): void {
        sessionStore.RemoveChangeListener(this.boundEventId);
        $(window).off("resize.login");
    }

    public render(): JSX.Element {
        let errorMessageJsx = null;
        let errorMessageStr = this.state.model.get("ErrorMessage");
        let requestResetPasswordErrorMessage = this.state.model.get("RequestResetPasswordErrorMessage");

        if (errorMessageStr) {
            errorMessageJsx = (
                <div data-cy="login-error-message" className="error-message">
                    <strong>
                        {errorMessageStr.UseLocalResource
                            ? this.stringProvider.GetString(errorMessageStr.Message)
                            : errorMessageStr.Message
                        }
                        {errorMessageStr.AccountActiveDate
                            && `${errorMessageStr.UseLocalResource ? "" : "Activation Date"}: `
                        }
                        {errorMessageStr.AccountActiveDate
                            && moment(errorMessageStr.AccountActiveDate).format("YYYY-MM-DD")
                        }
                    </strong>
                </div>
            );
        }

        return (
            <div className="myle-login">
                {this.showOverlay() && <div className="global-overlay translucent" />}

                <VersionCheck />

                {sessionStore.loginWorkflow && (
                    <LoginWorkflowPopups
                        stringProvider={this.stringProvider}
                        loginWorkflow={this.state.loginWorkflow}
                        loginModel={this.state.model}
                        password={this.state.password}
                        onPasswordChange={newPassword => this.mutateState(s => s.password = newPassword)}
                    />
                )}

                {this.state.model.get("OpenRequestResetPasswordControl") && (
                    <RequestResetPasswordControl
                        popupTitle="PasswordRecoveryTitle"
                        stringProvider={this.stringProvider}
                        currentlyProcessing={this.state.model.get("RequestResetPasswordInProgress")}
                        success={this.state.model.get("RequestResetPasswordSuccessful")}
                        errors={requestResetPasswordErrorMessage != null ? requestResetPasswordErrorMessage.Message : null}
                        onClosePopupCallBack={() => Dispatcher.dispatch(LocalOperations.Login.CloseRequestResetPasswordControl)}
                        onResetPassword={emailAddress =>
                            Dispatcher.dispatch(LocalOperations.Login.RequestResetPassword, {
                                emailAddress: emailAddress,
                                language: this.language
                            })
                        }
                    />
                )}

                {this.state.model.get("ShowPrivacyPolicy") && sessionStore.acceptedAgreement !== false && (
                    <Popup
                        cssClass="double-booking"
                        title={this.stringProvider.GetString("DisclaimerHeaderLabel")}
                        closeTarget={LocalOperations.Login.ClosePrivacyPolicy}
                    >
                        <div>
                            <div className="p-body">
                                <div className="row">
                                    <div className="col-sm-12 col-md-12 col-lg-12">
                                        {this.stringProvider.GetString("DisclaimerContent")}
                                    </div>
                                </div>
                            </div>
                            <div className="p-footer">
                                <div className="row">
                                    <div className="col-lg-12 col-md-12 col-sm-12 columns text-right">
                                        <button
                                            className="btn btn-primary btn-sm"
                                            disabled={false}
                                            onClick={() => sessionStore.dispatch(LocalOperations.Login.ClosePrivacyPolicy)}
                                        >
                                            {this.stringProvider.GetString("OKLabel")}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Popup>
                )}
                <div className="flex-container  myle-login-page" style={{ overflowY: "hidden" }}>
                    <div className="form-side">{this.renderForm(errorMessageJsx)}</div>

                    <div id="myle-wall" className="promo-banner">
                        <iframe
                            id="myle-wall-if"
                            src=""
                            style={{ height: "100%", width: "100%", overflow: "hidden" }}
                        />
                    </div>
                    <AsyncLoaderControl IsActive={sessionStore.loading} />
                </div>
                <Toast />
            </div>
        );
    }

    private showPhoneNumbers(): JSX.Element {
        const differentCountries = Array.from(new Set(sessionStore.ClinicActiveProvinces?.map(province => province.CountryLookupCode)));
        const differentStates = Array.from(new Set(sessionStore.ClinicActiveProvinces?.map(province => province.StateLookupCode)));

        if (differentCountries.length > 1 || differentStates.length > 1) {
            return (<></>);
        }

        // The lookupIds are hardcoded since there is no lookupProvider at this stage
        switch (differentStates?.[0]) {
            case CanadaState.Ontario:
                return (
                    <p>{this.stringProvider.GetString("OntarioPhoneLabel")}</p>
                );
            case CanadaState.Quebec:
                return (
                    <p>{this.stringProvider.GetString("QuebecPhoneLabel")}</p>
                );
            case CanadaState.BritishColumbia:
                return (
                    <p>{this.stringProvider.GetString("BritishColumbiaPhoneLabel")}</p>
                );
            default:
                return (<></>);
        }
    }

    private renderForm(errorMessageJsx: JSX.Element): JSX.Element {
        const helpURL = `https://medfar.my.site.com/s/?language=${this.language}`;

        let trackApplicationStatusURL;

        switch (this.language) {
            case Locale.fr:
                trackApplicationStatusURL = "https://statut.medfarsolutions.com";
                break;
            case Locale.es:
                trackApplicationStatusURL = "https://estatus.medfarsolutions.com";
                break;
            default:
                trackApplicationStatusURL = "https://status.medfarsolutions.com";
        }

        return (
            <div className="login-left-panel">
                <div className="f-row text-center">
                    <MyleLogo locale={this.language} className="medfar-logo" />
                </div>

                <div className="login-form">
                    {this.state.model.get("ShowAuthenticatorCodeForm")
                        ? (
                            <LoginAuthenticatorCodeView
                                state={this.state}
                                stringProvider={this.stringProvider}
                                errorMessageJsx={errorMessageJsx}
                            />
                        )
                        : (
                            <LoginCredentialsView
                                userName={this.state.model.get("UserName")}
                                validatingCredentials={this.state.model.get("ValidatingCredentials")}
                                isLoggedIn={this.state.model.get("LoggedIn")}
                                stringProvider={this.stringProvider}
                                errorMessageJsx={errorMessageJsx}
                                buttonTextLabelKey="login"
                                linkTextLabelKey="ForgotPasswordLabel"
                                onUsernameChange={username => this.onUsernameChange(username)}
                                onPasswordChange={Password => this.onPasswordChange(Password)}
                                onLinkClick={() => sessionStore.dispatch(LocalOperations.Login.OpenRequestResetPasswordControl)}
                                onSubmit={() => sessionStore.dispatch(LocalOperations.Login.OnLoginSubmit)}
                            />
                        )
                    }

                    <div className="f-row" style={{ textAlign: "center" }}>
                        <div className="phone-numbers-wrapper flex-row-gap-xs margin-xl-top margin-xs-bottom">
                            {this.showPhoneNumbers()}
                            <p>{this.stringProvider.GetString("TollFreePhoneLabel")}</p>
                            <p><a href={trackApplicationStatusURL} target="_blank">{this.stringProvider.GetString("trackApplicationStatus")}</a></p>
                        </div>
                        <ul className="login-links clearfix">
                            {navigator.platform.toLowerCase().indexOf("win") >= 0 && (
                                <li>
                                    <span>&gt; </span>
                                    <a href="https://download.teamviewer.com/download/version_15x/TeamViewerQS_en.exe">
                                        {this.stringProvider.GetString("remoteControl")}
                                    </a>
                                </li>
                            )}
                            {navigator.platform.toLowerCase().indexOf("win") < 0 && (
                                <li>
                                    <span>&gt; </span>
                                    <a href="https://download.teamviewer.com/download/version_15x/TeamViewerQS.dmg">
                                        {this.stringProvider.GetString("remoteControl")}
                                    </a>
                                </li>
                            )}
                            <li>
                                <span>&gt; </span>
                                <a onClick={() => this.onShowPrivacyPolicyClick()}>
                                    {this.stringProvider.GetString("privacyPolicy")}
                                </a>
                            </li>
                        </ul>
                    </div>
                    <div id="Version" className="f-row" style={{ textAlign: "center" }}>
                        <span> {sessionStore.versionNumber} </span>
                    </div>
                </div>

                <form className="sub-form">
                    <div className="form-group">
                        <button
                            onClick={() => window.open(helpURL)}
                            disabled={this.state.model.get("ValidatingCredentials")}
                            className="btn btn-default"
                        >
                            {this.stringProvider.GetString("MyleHelpLabel")}
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    private getFirstLanguage(lang: string): Locale {
        switch ((lang || "").toLowerCase()) {
            case "fr":
            case "fr-fr":
            case "fr-ca":
                return Locale.fr;

            case "en":
            case "en-us":
            case "en-ca":
                return Locale.en;

            case "es":
            case "es-mx":
                return Locale.es;

            default:
                return Locale.en;
        }
    }

    private onShowPrivacyPolicyClick(): void {
        sessionStore.dispatch(LocalOperations.Login.ShowPrivacyPolicy);
    }

    private storeChangeListener(): void {
        this.mutateState(s => {
            s.model = sessionStore.clientLoginModel;
            s.loginWorkflow = sessionStore.loginWorkflow;
        });
    }

    private maxHeight(): number {
        const h1 = $(".form-side").outerHeight(true);
        const h2 = $(window).height();
        return h1 > h2
            ? h1
            : h2;
    }

    private onResize(): void {
        const h = this.maxHeight();
        $("#myle-wall").css({ height: h + "px" });
    }

    private async getFrontPageCampaign(): Promise<void> {
        try {
            const response = await Client.postAsync<{ FrontpageId: number }>(ServerOperations.Server.Myle_GetFrontpageCampaign, {});
            this.mutateState(s => {
                if (response.errors?.length)
                    s.frontpageCampaignInError = true;
                else {
                    s.frontpageCampaignId = response.data.FrontpageId;
                    s.frontpageCampaignInError = false;
                }
            });
        }
        catch {
            this.mutateState(s => s.frontpageCampaignInError = true);
        }
    }

    private showOverlay(): boolean {
        return (
            this.state.model.get("ShowPrivacyPolicy") ||
            this.state.model.get("OpenRequestResetPasswordControl")
        );
    }

    public onUsernameChange(username: string): void {
        sessionStore.dispatch(LocalOperations.Login.Username_TextChange, { UserName: username });
    }

    public onPasswordChange(password: string): void {
        sessionStore.dispatch(LocalOperations.Login.Password_TextChange, { Password: password });
    }
}

export { LoginPage, LoginPageState };
